import React, { useEffect, useRef, useState } from 'react'
import { LoaderCircle, Search } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { Input } from 'shadcdn/ui/input'
import { SoftwareApi, SoftwareListSchema } from 'openapi'
import { cn } from 'utils/cn'
import { SoftwareRoutePathOld } from 'router'

export function useSoftwareSearch(search: string) {
  return useQuery({
    queryKey: ['searchSoftware', search],
    placeholderData: (prev) => prev,
    queryFn: () => {
      if (!search) return []
      return SoftwareApi.list({
        search,
        limit: 10,
        order: 'name',
        status: 'enabled',
      })
    },
  })
}

export const SearchInput: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const { data: searchResults, isFetching } = useSoftwareSearch(searchTerm)

  const [showDropdown, setShowDropdown] = useState<boolean>(false)

  const navigate = useNavigate()
  const handleSearchResultClick = (softwareId: number) => {
    setShowDropdown(false)
    navigate(SoftwareRoutePathOld({ softwareId }))
  }

  useEffect(() => {
    if (searchTerm) {
      setShowDropdown(true)
      setSelectedIndex(-1)
    }
  }, [searchTerm])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setShowDropdown(false)
      ;(e.target as HTMLInputElement).blur()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prevIndex) => Math.min(prevIndex + 1, (searchResults?.length || 1) - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prevIndex) => Math.max(prevIndex - 1, -1))
    } else if (e.key === 'Enter') {
      if (selectedIndex !== -1 && searchResults) {
        handleSearchResultClick(searchResults[selectedIndex].id)
        ;(e.target as HTMLInputElement).blur()
      }
    }
  }

  const wrapperRef = useRef(null)
  useOutsideAlerter(wrapperRef, () => setShowDropdown(false))

  const showSearchResults = !isFetching && searchTerm

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }
  return (
    <div className="relative w-96" ref={wrapperRef}>
      <div className="relative">
        <Input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowDropdown(!!searchTerm)}
          className={cn(
            'pl-6 pr-4 w-full text-xs text-gray-500 truncate py-1 rounded border border-gray-300 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
            { 'rounded-b-none': showSearchResults }
          )}
        />
        {isFetching ? (
          <LoaderCircle className="absolute top-[0.4rem] left-[0.4rem] text-gray-400 animate-spin" size={14} />
        ) : (
          <Search className="absolute top-[0.4rem] left-[0.4rem] text-gray-400" size={14} />
        )}
      </div>

      {showDropdown && (
        <div className="absolute top-[1.35rem] w text-xs z-10 w-full mt-1 bg-white border border-gray-300 rounded-b shadow-lg">
          <SearchResults
            searchResults={searchResults ?? []}
            onItemClick={handleSearchResultClick}
            selectedIndex={selectedIndex}
          />
        </div>
      )}
    </div>
  )
}

const SearchResults = (props: {
  searchResults: SoftwareListSchema[]
  onItemClick: (softwareId: number) => void
  selectedIndex: number
}) => {
  const { searchResults, selectedIndex } = props

  if (searchResults.length === 0) {
    return <div className="px-4 py-2 text-gray-500">No results found</div>
  }

  return (
    <ul>
      {searchResults.map((result, index) => (
        <li
          key={result.id}
          className={cn('p-2 hover:bg-gray-100 cursor-pointer', {
            'bg-gray-200': index === selectedIndex,
          })}
          onClick={() => props.onItemClick(result.id)}
        >
          {result.name} by <span className="text-gray-400">{result.vendor}</span>
        </li>
      ))}
    </ul>
  )
}

function useOutsideAlerter(ref: React.RefObject<HTMLElement>, callback: () => void) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [callback, ref])
}
